import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { waitlistAPI, sedesAPI } from '../../services/api';
import { Clock, UserPlus, Loader2, MapPin, Calendar, User, MessageSquare } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const WaitlistPage = () => {
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSedes();
  }, []);

  useEffect(() => {
    if (selectedSede) {
      loadWaitlist();
    }
  }, [selectedSede]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) {
        setSelectedSede(response.data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWaitlist = async () => {
    setLoading(true);
    try {
      const response = await waitlistAPI.getAll({ sede_id: selectedSede });
      setWaitlist(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (id) => {
    try {
      await waitlistAPI.assign(id);
      toast.success('Cupo asignado al colaborador');
      loadWaitlist();
    } catch (error) {
      toast.error('Error al asignar');
    }
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Lista de Espera
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Solicitudes de colaboradores para días adicionales de asistencia presencial
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">{waitlist.length} solicitudes</span>
            <Select value={selectedSede} onValueChange={setSelectedSede}>
              <SelectTrigger className="w-[220px] rounded-sm" data-testid="waitlist-sede-select">
                <SelectValue placeholder="Filtrar por sede" />
              </SelectTrigger>
              <SelectContent>
                {sedes.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : waitlist.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Clock className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay solicitudes en lista de espera para esta sede</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {waitlist.map((w) => (
              <Card key={w.id} className="border border-slate-200 hover:shadow-sm transition-shadow" data-testid={`waitlist-${w.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-amber-700" />
                        </div>
                        <div>
                          <p className="font-semibold text-base">{w.user_name}</p>
                          <p className="text-xs text-slate-400">{w.user_email}</p>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-medium">
                          Esperando
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                          Prioridad: {w.priority_score?.toFixed(0)}
                        </span>
                      </div>

                      {/* Date + Location */}
                      <div className="flex items-center gap-4 ml-12">
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-md border border-blue-100">
                          <Calendar className="w-3.5 h-3.5 text-blue-600" />
                          <div>
                            <p className="text-xs text-blue-700 font-semibold">{w.requested_day}</p>
                            <p className="text-[11px] text-blue-500">{w.requested_date_display}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
                          <MapPin className="w-3.5 h-3.5 text-slate-500" />
                          <p className="text-xs text-slate-600 font-medium">{w.sede_name}</p>
                        </div>
                      </div>

                      {/* Reason */}
                      {w.reason && (
                        <div className="ml-12 p-3 bg-slate-50 rounded-md border border-slate-100 flex items-start gap-2">
                          <MessageSquare className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                          <p className="text-sm text-slate-600 leading-relaxed">"{w.reason}"</p>
                        </div>
                      )}

                      {/* Created at */}
                      <p className="text-[11px] text-slate-400 ml-12">Solicitado el {w.created_at_display}</p>
                    </div>

                    {/* Action */}
                    <div className="shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleAssign(w.id)}
                        className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm h-9 px-4"
                        data-testid={`assign-${w.id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1.5" />
                        Asignar cupo
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default WaitlistPage;
